import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import {
  HttpError,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPDFLabelSetRepository } from '../../../business-logic/interfaces/IPDFLabelSetRepository'
import { PDFLabelSections } from '../../../entities/enums/PDFLabelSectionEnum'
import {
  type PDFLabelFieldMetadataConstructor,
  type PDFLabelSetConstructor,
} from '../../../entities/PDFLabelSet'
import {
  type PDFLabelFieldMetadataApiModel,
  type PDFLabelFieldMetadataEntryApiModel,
} from '../../api-models/PDFLabelFieldMetadataApiModel'
import { type PDFLabelSetApiModel } from '../../api-models/PDFLabelSetApiModel'
import { mapPDFLabelSetApiModel } from '../../mappers/mapPDFLabelSetApiModel'
import {
  mapPDFLabelSetConstructorToApiModel,
  ORDER_ITEM_LABEL_API_KEY_BY_DOMAIN_KEY,
  PO_LABEL_API_KEY_BY_DOMAIN_KEY,
} from '../../mappers/mapPDFLabelSetConstructorToApiModel'
import { type GetPDFLabelSetsResponse } from '../../types/GetPDFLabelSetsResponse'

const PDF_LABEL_SET_URL = '/api/v3/kvstore/translations/'
const PRIMARY_TRANSLATION_URL = '/api/v3/kvstore/primary-translation/'

interface Dependency {
  httpClient: IHttpClient
}

const invertKeyMap = (map: Record<string, string>): Record<string, string> =>
  Object.keys(map).reduce<Record<string, string>>((acc, domainKey) => {
    acc[map[domainKey]] = domainKey
    return acc
  }, {})

const PO_LABEL_DOMAIN_KEY_BY_API_KEY = invertKeyMap(
  PO_LABEL_API_KEY_BY_DOMAIN_KEY
)
const ORDER_ITEM_LABEL_DOMAIN_KEY_BY_API_KEY = invertKeyMap(
  ORDER_ITEM_LABEL_API_KEY_BY_DOMAIN_KEY
)

export class PDFLabelSetRepository implements IPDFLabelSetRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<PDFLabelSetConstructor>> {
    let apiParams: Record<string, unknown> =
      formatRepoFiltersToApiFilters(params)

    // PDF Label Set API uses '9999' to fetch all records when no pagination is specified
    if (!params?.page) {
      apiParams = {
        ...apiParams,
        page_size: 9999,
      }
    }

    const res = await this.deps.httpClient.get<GetPDFLabelSetsResponse>(
      PDF_LABEL_SET_URL,
      { params: apiParams }
    )

    const labelSets = res.data.data || []
    const metadata = res.data.metadata

    return {
      data: labelSets.map((labelSet) => mapPDFLabelSetApiModel(labelSet)),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  async get(key: string): Promise<PDFLabelSetConstructor> {
    try {
      const res = await this.deps.httpClient.get<{
        data: PDFLabelSetApiModel
      }>(`${PDF_LABEL_SET_URL}${key}/`)

      return mapPDFLabelSetApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getPrimary(): Promise<PDFLabelSetConstructor | null> {
    try {
      const res = await this.deps.httpClient.get<{
        data: PDFLabelSetApiModel
      }>(PRIMARY_TRANSLATION_URL)

      return mapPDFLabelSetApiModel(res.data.data)
    } catch (err) {
      // 404 is a valid "no primary set" answer, not an error
      if (err instanceof HttpError && err.status === 404) {
        return null
      }

      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getFieldMetadata(): Promise<PDFLabelFieldMetadataConstructor[]> {
    try {
      const res = await this.deps.httpClient.options<{
        data: PDFLabelFieldMetadataApiModel
      }>(PDF_LABEL_SET_URL)

      const children = res.data.data.actions.POST.value.children

      const mapEntries = (
        entries: Record<string, PDFLabelFieldMetadataEntryApiModel>,
        domainKeyByApiKey: Record<string, string>,
        defaultSection: PDFLabelFieldMetadataConstructor['section']
      ): PDFLabelFieldMetadataConstructor[] =>
        Object.keys(entries).map((apiKey) => ({
          fieldKey: domainKeyByApiKey[apiKey] ?? apiKey,
          defaultLabel: entries[apiKey].label,
          // Provisional group-based section — the getPOPDFLabelEditorFields
          // Use Case owns the final LABEL_SECTION_MAP assignment
          section: defaultSection,
          maxLength: entries[apiKey].max_length,
          isCustomFieldLabel: false,
        }))

      return [
        ...mapEntries(
          children.po.children,
          PO_LABEL_DOMAIN_KEY_BY_API_KEY,
          PDFLabelSections.PO_FIELDS
        ),
        ...mapEntries(
          children.orderitem.children,
          ORDER_ITEM_LABEL_DOMAIN_KEY_BY_API_KEY,
          PDFLabelSections.ITEM_FIELDS
        ),
      ]
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async create(payload: PDFLabelSetConstructor): Promise<{ key: string }> {
    try {
      const res = await this.deps.httpClient.post<{
        data: PDFLabelSetApiModel
      }>(PDF_LABEL_SET_URL, mapPDFLabelSetConstructorToApiModel(payload))

      return { key: res.data.data?.key ?? payload.key }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(
    key: string,
    payload: PDFLabelSetConstructor
  ): Promise<{ key: string }> {
    try {
      const res = await this.deps.httpClient.put<{
        data: PDFLabelSetApiModel
      }>(
        `${PDF_LABEL_SET_URL}${key}/`,
        mapPDFLabelSetConstructorToApiModel(payload)
      )

      // The backend canonicalizes keys (spaces → hyphens); return the stored key
      return { key: res.data.data?.key ?? payload.key }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async delete(key: string): Promise<void> {
    try {
      await this.deps.httpClient.delete(`${PDF_LABEL_SET_URL}${key}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async setPrimary(key: string): Promise<void> {
    try {
      await this.deps.httpClient.post(PRIMARY_TRANSLATION_URL, { value: key })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updatePrimary(key: string): Promise<void> {
    try {
      await this.deps.httpClient.put(PRIMARY_TRANSLATION_URL, { value: key })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async clearPrimary(): Promise<void> {
    try {
      await this.deps.httpClient.delete(PRIMARY_TRANSLATION_URL)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
